import { useEffect, useMemo, useState } from 'react';
import { PAGE_SIZE_OPTIONS } from '@/constants/pagination';
import type { ReactNode } from 'react';
import {
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import type { TableCellProps } from '@mui/material';
import { Print as PrintIcon, West as WestIcon } from '@mui/icons-material';
import { FullScreenModal } from '@/components/shared/FullScreenModal';
import { useNotifications } from '@/stores/useNotificationStore';

export type CustomerSummaryPrintCell = {
  primary: string;
  secondary?: string;
};

export type CustomerSummaryPrintValue = string | number | CustomerSummaryPrintCell;

export type CustomerSummaryColumn<Row> = {
  header: string;
  align?: TableCellProps['align'];
  width?: string | number;
  render: (row: Row) => ReactNode;
  printValue: (row: Row) => CustomerSummaryPrintValue;
};

export type CustomerSummarySection<Row> = {
  key: string;
  title: string;
  rows: Row[];
  columns: Array<CustomerSummaryColumn<Row>>;
  getRowKey: (row: Row) => string;
  pagination?: {
    defaultRowsPerPage?: number;
    rowsPerPageOptions?: number[];
  };
  printSummary?: {
    label: string;
    value: string;
  };
};

// Type-erased version for props that accept heterogeneous sections
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type CustomerSummarySectionAny = CustomerSummarySection<any>;

export interface CustomerSummaryModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  workflowLabel: string;
  unitName?: string;
  memberName?: string;
  subtitle?: string;
  summaryLeft?: ReactNode;
  summaryRight?: ReactNode;
  sections: CustomerSummarySectionAny[];
  generatedAt: Date;
  printTitle?: string;
  printSubtitle?: string;
  printButtonLabel?: string;
  printWindowFeatures?: string;
  closeLabel?: string;
  backLabel?: string;
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatDateTime = (value: Date): string =>
  new Intl.DateTimeFormat('en-US', {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(value);

const isStackedPrintCell = (value: CustomerSummaryPrintValue): value is CustomerSummaryPrintCell =>
  typeof value === 'object' && value !== null && 'primary' in value;

const buildPrintCellMarkup = (
  value: CustomerSummaryPrintValue,
  align: TableCellProps['align'] | undefined,
): string => {
  const className = align === 'right' ? 'numeric' : '';
  const classAttribute = className ? ` class="${className}"` : '';

  if (isStackedPrintCell(value)) {
    const primary = escapeHtml(value.primary);
    const secondary = value.secondary?.trim()
      ? `<div class="meta">${escapeHtml(value.secondary.trim())}</div>`
      : '';
    return `<td${classAttribute}><div class="name">${primary}</div>${secondary}</td>`;
  }

  const text = escapeHtml(String(value ?? ''));
  return `<td${classAttribute}>${text}</td>`;
};

const buildCustomerSummaryPrintHtml = (options: {
  generatedAt: Date;
  title: string;
  subtitle?: string;
  sections: CustomerSummarySectionAny[];
}) => {
  const generatedLabel = formatDateTime(options.generatedAt);
  const subtitle = options.subtitle?.trim() ? options.subtitle.trim() : '';

  const sectionMarkup = options.sections
    .filter((section) => section.rows.length > 0)
    .map((section) => {
      const headerMarkup = section.columns
        .map((column) => {
          const className = column.align === 'right' ? 'numeric' : '';
          const classAttribute = className ? ` class="${className}"` : '';
          return `<th${classAttribute}>${escapeHtml(column.header)}</th>`;
        })
        .join('');

      const rowsMarkup = section.rows
        .map((row) => {
          const cellsMarkup = section.columns
            .map((column) => buildPrintCellMarkup(column.printValue(row), column.align))
            .join('');
          return `<tr>${cellsMarkup}</tr>`;
        })
        .join('');

      const summaryMarkup = section.printSummary
        ? `<div class="totals">${escapeHtml(section.printSummary.label)} <strong>${escapeHtml(
            section.printSummary.value,
          )}</strong></div>`
        : '';

      return `<section>
        <h2>${escapeHtml(section.title)}</h2>
        <table>
          <thead>
            <tr>
              ${headerMarkup}
            </tr>
          </thead>
          <tbody>
            ${rowsMarkup}
          </tbody>
        </table>
        ${summaryMarkup}
      </section>`;
    })
    .join('');

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>${escapeHtml(options.title)}</title>
    <style>
      :root {
        color-scheme: light;
      }
      body {
        font-family: 'Roboto', Arial, sans-serif;
        margin: 24px;
        color: #111827;
        background: #ffffff;
      }
      header {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        gap: 16px;
        margin-bottom: 16px;
      }
      h1 {
        margin: 0;
        font-size: 20px;
        letter-spacing: 0.2px;
      }
      .subtitle {
        margin: 2px 0 0 0;
        font-size: 13px;
        color: #4b5563;
      }
      .generated {
        font-size: 12px;
        color: #6b7280;
        white-space: nowrap;
      }
      section {
        margin-top: 18px;
      }
      h2 {
        margin: 0 0 8px 0;
        font-size: 14px;
        color: #111827;
        letter-spacing: 0.2px;
      }
      table {
        width: 100%;
        border-collapse: collapse;
      }
      th, td {
        border: 1px solid #e5e7eb;
        padding: 8px 10px;
        font-size: 12px;
        vertical-align: top;
      }
      th {
        background: #f9fafb;
        text-align: left;
        font-weight: 700;
      }
      td.numeric, th.numeric {
        text-align: right;
        white-space: nowrap;
      }
      .name {
        font-weight: 600;
      }
      .meta {
        margin-top: 2px;
        color: #6b7280;
        font-size: 11px;
      }
      .totals {
        display: flex;
        justify-content: flex-end;
        margin-top: 8px;
        font-size: 12px;
        color: #111827;
      }
      .totals strong {
        margin-left: 10px;
      }
      @media print {
        body {
          margin: 0;
        }
      }
    </style>
  </head>
  <body>
    <header>
      <div>
        <h1>${escapeHtml(options.title)}</h1>
        ${subtitle ? `<div class="subtitle">${escapeHtml(subtitle)}</div>` : ''}
      </div>
      <div class="generated">Generated ${escapeHtml(generatedLabel)}</div>
    </header>

    ${sectionMarkup}

    <script>
      window.addEventListener('load', () => {
        window.focus();
        window.print();
      });
    </script>
  </body>
</html>`;
};

const buildSubtitle = (subtitle: string | undefined, unitName?: string, memberName?: string) => {
  if (subtitle && subtitle.trim()) {
    return subtitle.trim();
  }

  const composed = [unitName, memberName].filter(Boolean).join(' \u2022 ');
  return composed || undefined;
};

const CustomerSummarySectionTable = <Row,>({
  open,
  section,
}: {
  open: boolean;
  section: CustomerSummarySection<Row>;
}) => {
  const { rows, columns, getRowKey, pagination } = section;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(pagination?.defaultRowsPerPage ?? 25);

  useEffect(() => {
    if (!open) {
      setPage(0);
    }
  }, [open]);

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return rows.slice(start, start + rowsPerPage);
  }, [page, rows, rowsPerPage]);

  return (
    <Stack spacing={1.5}>
      <Typography variant="h6" component="h3">
        {section.title}
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.header}
                  align={column.align}
                  sx={{
                    width: column.width,
                    px: 2.5,
                    py: 2,
                    fontWeight: 600,
                  }}
                >
                  {column.header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.map((row) => (
              <TableRow key={getRowKey(row)} hover>
                {columns.map((column) => (
                  <TableCell
                    key={`${getRowKey(row)}:${column.header}`}
                    align={column.align}
                    sx={{
                      width: column.width,
                      px: 2.5,
                      py: 1.75,
                    }}
                  >
                    {column.render(row)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={(_event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={pagination?.rowsPerPageOptions ?? PAGE_SIZE_OPTIONS}
        />
      </TableContainer>
    </Stack>
  );
};

export const CustomerSummaryModal = ({
  open,
  onClose,
  title,
  workflowLabel,
  unitName,
  memberName,
  subtitle,
  summaryLeft,
  summaryRight,
  sections,
  generatedAt,
  printTitle,
  printSubtitle,
  printButtonLabel,
  printWindowFeatures,
  closeLabel,
  backLabel,
}: CustomerSummaryModalProps) => {
  const notifications = useNotifications();
  const resolvedSubtitle = useMemo(
    () => buildSubtitle(subtitle, unitName, memberName),
    [memberName, subtitle, unitName],
  );

  const handlePrint = () => {
    const printWindow = window.open('', '_blank', printWindowFeatures ?? 'width=960,height=720');
    if (!printWindow) {
      notifications.error('Print failed', 'Pop-up blocked. Allow pop-ups to print the summary.');
      return;
    }

    const html = buildCustomerSummaryPrintHtml({
      generatedAt,
      title: printTitle ?? title,
      subtitle: printSubtitle ?? resolvedSubtitle,
      sections,
    });

    printWindow.document.write(html);
    printWindow.document.close();
  };

  const visibleSections = useMemo(
    () => sections.filter((section) => section.rows.length > 0),
    [sections],
  );

  return (
    <FullScreenModal
      open={open}
      onClose={onClose}
      title={title}
      subTitle={resolvedSubtitle}
      closeLabel={closeLabel ?? `Return to ${workflowLabel.toLowerCase()} workflow`}
      headerActions={
        <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}>
          {printButtonLabel ?? 'Print'}
        </Button>
      }
      closeButton={
        <Button variant="outlined" startIcon={<WestIcon />} onClick={onClose}>
          {backLabel ?? `Back to ${workflowLabel}`}
        </Button>
      }
    >
      <Stack spacing={3}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              {summaryLeft ?? 'Please review the items below before signing.'}
            </Typography>
            {summaryRight ? (
              <Stack direction="row" spacing={2}>
                {summaryRight}
              </Stack>
            ) : null}
          </Stack>
        </Paper>

        {visibleSections.map((section) => (
          <CustomerSummarySectionTable key={section.key} open={open} section={section} />
        ))}

        <Typography variant="caption" color="text.secondary">
          Generated {formatDateTime(generatedAt)}
        </Typography>
      </Stack>
    </FullScreenModal>
  );
};
